import React from 'react'

import classNames from 'classnames'

import { Box, BoxProps } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Icon } from 'ui/components/Icon'
import { Progress } from 'ui/components/Progress'
import { Body } from 'ui/components/Text'
import { Tooltip } from 'ui/components/Tooltip'

import { useFileUploadFileListItemState } from './hooks/useFileUploadFileListItemState'
import { FileUploadItemContext, useFileUploadItemContext } from './hooks/useFileUploadItemContext'
import { useFileUploadItemMetadataState } from './hooks/useFileUploadItemMetadataState'
import { useFileUploadItemPreviewState } from './hooks/useFileUploadItemPreviewState'
import { useFileUploadItemProgressState } from './hooks/useFileUploadItemProgressState'
import { useFileUploadItemRemoveButtonState } from './hooks/useFileUploadItemRemoveButtonState'
import { FileState } from './types'

import {
    FileUploadFileListItemStyle,
    FileUploadItemPreviewImageStyles,
    FileUploadItemPreviewStyles,
    FileUploadItemStatusStyles,
} from './FileUpload.css'

type FileUploadFileListItemProps = BoxProps & {
    value: FileState
    fileIndex: number
    fileCount: number
}

export const FileUploadFileListItem: React.FC<FileUploadFileListItemProps> = ({
    value,
    fileIndex,
    fileCount,
    className,
    ...props
}) => {
    const { id, nameId, sizeId, statusId, itemContext, statusText } =
        useFileUploadFileListItemState({ value })

    return (
        <Box
            role="listitem"
            id={id}
            aria-setsize={fileCount}
            aria-posinset={fileIndex}
            aria-describedby={`${nameId} ${sizeId} ${statusId}`}
            aria-labelledby={nameId}
            className={classNames(FileUploadFileListItemStyle, className)}
            {...props}
        >
            <FileUploadItemContext.Provider value={itemContext}>
                <FileUploadItemPreview noShrink />
                <Box flex column minWidth={0} grow gap="xs">
                    <FileUploadItemMetadata />
                    <FileUploadItemProgress />
                </Box>
                <FileUploadItemRemoveButton noShrink />
                <span id={statusId} className="sr-only">
                    {statusText}
                </span>
            </FileUploadItemContext.Provider>
        </Box>
    )
}

type FileUploadItemPreviewProps = BoxProps & {}

const FileUploadItemPreview: React.FC<FileUploadItemPreviewProps> = (props) => {
    const { nameId, imageSrc, fileName, fileIcon, hasImagePreview, onImageLoad, isImageLoaded } =
        useFileUploadItemPreviewState()

    return (
        <Box position="relative" {...props}>
            <Box
                aria-labelledby={nameId}
                className={FileUploadItemPreviewStyles.styleFunction({
                    hasImage: hasImagePreview,
                })}
            >
                {hasImagePreview && (
                    <Box
                        as="img"
                        src={imageSrc!}
                        alt={fileName}
                        className={FileUploadItemPreviewImageStyles.styleFunction({
                            isLoaded: isImageLoaded,
                        })}
                        onLoad={onImageLoad}
                    />
                )}
                {!hasImagePreview && fileIcon && (
                    <Icon size="2xl" color="iconInverse" {...fileIcon} />
                )}
                <FileUploadItemStatus />
            </Box>
        </Box>
    )
}

type FileUploadItemMetadataProps = BoxProps & {}

const FileUploadItemMetadata: React.FC<FileUploadItemMetadataProps> = (props) => {
    const { nameId, sizeId, fileName, fileSize } = useFileUploadItemMetadataState()

    return (
        <Box minWidth={0} {...props}>
            <Box flex column minWidth={0}>
                <Body
                    id={nameId}
                    size="m"
                    weight="medium"
                    display="block"
                    maxWidth="full"
                    minWidth={0}
                    trim
                >
                    {fileName}
                </Body>
                <Body id={sizeId} size="s" color="textWeaker">
                    {fileSize}
                </Body>
            </Box>
        </Box>
    )
}

type FileUploadItemProgressProps = BoxProps & {}

const FileUploadItemProgress: React.FC<FileUploadItemProgressProps> = (props) => {
    const { progress, showProgress } = useFileUploadItemProgressState()

    if (!showProgress) return null

    return (
        <Box {...props}>
            {showProgress && <Progress value={progress} style={{ width: '100%' }} />}
        </Box>
    )
}

type FileUploadItemRemoveButtonProps = BoxProps & {}

const FileUploadItemRemoveButton: React.FC<FileUploadItemRemoveButtonProps> = (props) => {
    const { onClick } = useFileUploadItemRemoveButtonState()

    return (
        <Box {...props}>
            <Button size="s" variant="ghost" startIcon={{ name: 'X' }} onClick={onClick} />
        </Box>
    )
}

type FileUploadItemStatusProps = BoxProps & {}

const FileUploadItemStatus: React.FC<FileUploadItemStatusProps> = ({ className, ...props }) => {
    const { fileState } = useFileUploadItemContext()

    if (!fileState) return null

    const isError = fileState.status === 'error'
    const errorMessage = fileState.error

    const isSuccess = fileState.status === 'success'

    if (!isError && !isSuccess) return null

    return (
        <Box
            className={classNames(
                FileUploadItemStatusStyles.styleFunction({ status: fileState.status }),
                className
            )}
            {...props}
        >
            {isError && (
                <Tooltip content={errorMessage} zIndex={9999}>
                    <Icon name="X" color="iconInverse" size="xs" />
                </Tooltip>
            )}
            {isSuccess && <Icon name="Check" color="iconInverse" size="xs" />}
        </Box>
    )
}
